import { CountdownEvent, TimeDiff } from '../core/types';
import { calcTimeDiff, getEffectiveTarget, getUrgencyLevel, formatTimePart } from '../core/time';
import { createVirtualScroller, VIRTUAL_SCROLL_THRESHOLD, VirtualScroller } from './virtual-scroll';

let activeScroller: VirtualScroller | null = null;

export function destroyScroller(): void {
  if (activeScroller) {
    activeScroller.destroy();
    activeScroller = null;
  }
}

const ICONS = {
  share: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>',
  edit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>',
  archive: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/></svg>',
  restore: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
  repeat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>',
};

const CATEGORY_LABELS: Record<string, string> = {
  birthday: 'Birthday',
  holiday: 'Holiday',
  vacation: 'Vacation',
  deadline: 'Deadline',
};

export function renderDashboard(
  events: CountdownEvent[],
  compact: boolean,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onArchive: (id: string) => void,
  onShare: (id: string) => void,
  onCreate: () => void
): HTMLElement {
  destroyScroller();

  const container = document.createElement('section');
  container.className = 'view-container';
  container.id = 'main';
  container.setAttribute('role', 'main');
  container.setAttribute('aria-label', 'Active countdowns');

  const now = new Date();
  const active = events
    .filter(e => !e.completedAt)
    .map(e => ({ event: e, effectiveTarget: getEffectiveTarget(e, now) }))
    .map(({ event, effectiveTarget }) => ({
      event,
      diff: calcTimeDiff(effectiveTarget, now),
      effectiveTarget,
    }))
    .sort((a, b) => a.diff.totalMs - b.diff.totalMs);

  if (active.length === 0) {
    container.appendChild(buildDashboardEmptyState(onCreate));
    return container;
  }

  const meta = document.createElement('div');
  meta.className = 'view-meta';
  meta.innerHTML = `
    <h2>${compact ? 'In flight' : 'Active countdowns'}</h2>
    <span class="count" aria-label="${active.length} active countdowns"><b>${String(active.length).padStart(2, '0')}</b> &nbsp;pending</span>
  `;
  container.appendChild(meta);

  if (active.length > VIRTUAL_SCROLL_THRESHOLD && !compact) {
    const grid = document.createElement('div');
    grid.className = 'dashboard';
    grid.setAttribute('role', 'list');
    container.appendChild(grid);

    activeScroller = createVirtualScroller({
      container: grid,
      items: active,
      itemHeight: 280,
      bufferCount: 3,
      layout: 'grid',
      getKey: (item) => item.event.id,
      renderItem: (item, index) => {
        const card = createEventCard(item.event, item.diff, false, onEdit, onDelete, onArchive, onShare);
        card.style.setProperty('--i', String(index));
        return card;
      },
    });
  } else {
    const grid = document.createElement('div');
    grid.className = compact ? 'dashboard compact' : 'dashboard';
    grid.setAttribute('role', 'list');

    active.forEach(({ event, diff }, index) => {
      const card = createEventCard(event, diff, compact, onEdit, onDelete, onArchive, onShare);
      card.style.setProperty('--i', String(index));
      grid.appendChild(card);
    });

    container.appendChild(grid);
    observeCardEntrance(grid);
  }

  return container;
}

function observeCardEntrance(grid: HTMLElement): void {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('card-visible');
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '50px', threshold: 0.1 }
  );

  requestAnimationFrame(() => {
    grid.querySelectorAll('.event-card, .archive-card').forEach(card => {
      observer.observe(card);
    });
  });
}

function createEventCard(
  event: CountdownEvent,
  diff: TimeDiff,
  compact: boolean,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onArchive: (id: string) => void,
  onShare: (id: string) => void
): HTMLElement {
  const card = document.createElement('article');
  const urgency = diff.isPast ? 'past' : getUrgencyLevel(diff.totalMs);
  card.className = `event-card urgency-${urgency}`;
  card.setAttribute('role', 'listitem');
  card.setAttribute('aria-label', `${event.title} — ${formatAriaTime(diff)}`);
  card.style.setProperty('--card-color', event.color);

  const categoryLabel = CATEGORY_LABELS[event.category] ?? event.category;

  if (compact) {
    card.innerHTML = `
      <div class="card-compact">
        <span class="card-icon" aria-hidden="true">${escapeHtml(event.icon)}</span>
        <span class="card-title">${escapeHtml(event.title)}</span>
        <span class="card-time ${diff.isPast ? 'past' : ''}">${diff.isPast ? 'Reached' : formatCompact(diff)}</span>
      </div>
    `;
    card.addEventListener('click', () => onEdit(event.id));
    card.style.cursor = 'pointer';
    return card;
  }

  card.innerHTML = `
    <header class="card-header">
      <span class="card-icon" aria-hidden="true">${escapeHtml(event.icon)}</span>
      <h3 class="card-title">${escapeHtml(event.title)}</h3>
      <span class="card-category">${escapeHtml(categoryLabel)}</span>
    </header>
    <div class="card-countdown" data-event-id="${event.id}" aria-live="off">
      ${diff.isPast ? doneTemplate() : renderDigits(diff)}
    </div>
    <footer class="card-footer">
      <div>
        <span class="card-target-date">${formatTargetDate(event, diff)}</span>
        ${event.repeatYearly ? `<span class="badge repeat" aria-label="Repeats yearly">${ICONS.repeat}<span>Yearly</span></span>` : ''}
      </div>
      <div class="card-actions">
        <button class="btn-icon" type="button" data-action="share" aria-label="Share ${escapeHtml(event.title)}">${ICONS.share}</button>
        <button class="btn-icon" type="button" data-action="edit" aria-label="Edit ${escapeHtml(event.title)}">${ICONS.edit}</button>
        ${diff.isPast ? `<button class="btn-icon" type="button" data-action="archive" aria-label="Archive ${escapeHtml(event.title)}">${ICONS.archive}</button>` : ''}
        <button class="btn-icon" type="button" data-action="delete" aria-label="Delete ${escapeHtml(event.title)}">${ICONS.trash}</button>
      </div>
    </footer>
  `;

  card.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!btn) return;
    const action = btn.dataset.action;
    if (action === 'edit') onEdit(event.id);
    else if (action === 'delete') onDelete(event.id);
    else if (action === 'archive') onArchive(event.id);
    else if (action === 'share') onShare(event.id);
  });

  return card;
}

function renderDigits(diff: TimeDiff): string {
  return `
    <div class="digit-group">
      <span class="digit" aria-hidden="true">${String(diff.days).padStart(2, '0')}</span>
      <span class="digit-label">Days</span>
    </div>
    <span class="digit-sep" aria-hidden="true">:</span>
    <div class="digit-group">
      <span class="digit" aria-hidden="true">${formatTimePart(diff.hours)}</span>
      <span class="digit-label">Hrs</span>
    </div>
    <span class="digit-sep" aria-hidden="true">:</span>
    <div class="digit-group">
      <span class="digit" aria-hidden="true">${formatTimePart(diff.minutes)}</span>
      <span class="digit-label">Min</span>
    </div>
    <span class="digit-sep" aria-hidden="true">:</span>
    <div class="digit-group">
      <span class="digit" aria-hidden="true">${formatTimePart(diff.seconds)}</span>
      <span class="digit-label">Sec</span>
    </div>
  `;
}

function doneTemplate(): string {
  return `<div class="done-text">The moment arrived<small>celebrate, then archive</small></div>`;
}

function formatCompact(diff: TimeDiff): string {
  if (diff.days > 0) return `${diff.days}d ${diff.hours}h`;
  if (diff.hours > 0) return `${diff.hours}h ${diff.minutes}m`;
  return `${diff.minutes}m ${diff.seconds}s`;
}

function formatAriaTime(diff: TimeDiff): string {
  if (diff.isPast) return 'event reached';
  return `${diff.days} days, ${diff.hours} hours, ${diff.minutes} minutes remaining`;
}

function formatTargetDate(event: CountdownEvent, diff: TimeDiff): string {
  const target = new Date(diff.isPast ? event.targetDate : getEffectiveTarget(event));
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt.format(target);
}

function buildDashboardEmptyState(onCreate: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'empty-state';
  wrap.setAttribute('role', 'status');
  wrap.innerHTML = `
    <svg class="empty-art" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <circle cx="100" cy="100" r="92" stroke="currentColor" stroke-width="1" opacity=".18"/>
      <circle cx="100" cy="100" r="72" stroke="currentColor" stroke-width="1" opacity=".28"/>
      <circle cx="100" cy="100" r="52" stroke="currentColor" stroke-width="1.5" opacity=".5"/>
      <circle cx="100" cy="100" r="32" stroke="currentColor" stroke-width="2"/>
      <g stroke-linecap="round"><line x1="100" y1="22" x2="100" y2="32" stroke="currentColor" stroke-width="2"/><line x1="100" y1="168" x2="100" y2="178" stroke="currentColor" stroke-width="2"/><line x1="22" y1="100" x2="32" y2="100" stroke="currentColor" stroke-width="2"/><line x1="168" y1="100" x2="178" y2="100" stroke="currentColor" stroke-width="2"/></g>
      <line x1="100" y1="100" x2="100" y2="56" class="accent" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
      <line x1="100" y1="100" x2="138" y2="100" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity=".6"/>
      <circle cx="100" cy="100" r="5" class="accent" fill="currentColor"/>
    </svg>
    <p class="empty-eyebrow">No events yet</p>
    <h2>Mark something worth waiting for.</h2>
    <p>Add a date — a birthday, a flight, a deadline — and the clock will hold its breath until then.</p>
  `;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn-save';
  btn.setAttribute('aria-label', 'Create your first countdown');
  btn.innerHTML = `${ICONS.plus}<span>Start a countdown</span>`;
  btn.addEventListener('click', onCreate);
  wrap.appendChild(btn);

  return wrap;
}

function buildArchiveEmptyState(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'empty-state';
  wrap.setAttribute('role', 'status');
  wrap.innerHTML = `
    <svg class="empty-art" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <rect x="30" y="130" width="140" height="50" rx="6" stroke="currentColor" stroke-width="1.5" opacity=".25"/>
      <rect x="40" y="98" width="120" height="42" rx="5" stroke="currentColor" stroke-width="1.5" opacity=".45"/>
      <rect x="52" y="68" width="96" height="36" rx="4" stroke="currentColor" stroke-width="2" fill="var(--surface, transparent)"/>
      <line x1="70" y1="86" x2="130" y2="86" class="accent" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="70" y1="118" x2="110" y2="118" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity=".5"/>
      <line x1="70" y1="150" x2="100" y2="150" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity=".4"/>
      <circle cx="158" cy="50" r="6" class="accent" fill="currentColor"/>
      <line x1="148" y1="40" x2="168" y2="60" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity=".5"/>
    </svg>
    <p class="empty-eyebrow">The archive is quiet</p>
    <h2>No memories filed away yet.</h2>
    <p>When a countdown reaches its date, drop it here with a note. Past events become souvenirs.</p>
  `;
  return wrap;
}

export function renderArchive(
  events: CountdownEvent[],
  onRestore: (id: string) => void,
  onDelete: (id: string) => void,
  onMemory: (id: string, memory: string) => void
): HTMLElement {
  const container = document.createElement('section');
  container.className = 'view-container';
  container.id = 'main';
  container.setAttribute('role', 'main');
  container.setAttribute('aria-label', 'Archived events');

  const archived = events.filter(e => !!e.completedAt).sort(
    (a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime()
  );

  if (archived.length === 0) {
    container.appendChild(buildArchiveEmptyState());
    return container;
  }

  const meta = document.createElement('div');
  meta.className = 'view-meta';
  meta.innerHTML = `
    <h2>Memories</h2>
    <span class="count"><b>${String(archived.length).padStart(2, '0')}</b> &nbsp;filed</span>
  `;
  container.appendChild(meta);

  const list = document.createElement('div');
  list.className = 'archive';
  list.setAttribute('role', 'list');

  archived.forEach((event, index) => {
    const card = document.createElement('article');
    card.className = 'archive-card';
    card.setAttribute('role', 'listitem');
    card.style.setProperty('--card-color', event.color);
    card.style.setProperty('--i', String(index));
    card.innerHTML = `
      <header class="archive-header">
        <span class="card-icon" aria-hidden="true">${escapeHtml(event.icon)}</span>
        <h3>${escapeHtml(event.title)}</h3>
        <span class="archive-date">${new Date(event.completedAt!).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
      </header>
      ${event.memory ? `<p class="archive-memory">&ldquo;${escapeHtml(event.memory)}&rdquo;</p>` : ''}
      <div class="archive-actions">
        <input type="text" placeholder="Add a memory…" value="${escapeAttr(event.memory ?? '')}" class="memory-input" aria-label="Memory for ${escapeHtml(event.title)}" />
        <button class="btn-icon" type="button" data-action="restore" aria-label="Restore ${escapeHtml(event.title)}">${ICONS.restore}</button>
        <button class="btn-icon" type="button" data-action="delete" aria-label="Delete ${escapeHtml(event.title)}">${ICONS.trash}</button>
      </div>
    `;

    const input = card.querySelector('.memory-input') as HTMLInputElement;
    input.addEventListener('change', () => onMemory(event.id, input.value));
    card.querySelector('[data-action="restore"]')!.addEventListener('click', () => onRestore(event.id));
    card.querySelector('[data-action="delete"]')!.addEventListener('click', () => onDelete(event.id));

    list.appendChild(card);
  });

  container.appendChild(list);
  observeCardEntrance(list);
  return container;
}

export function renderSkeleton(count = 3): HTMLElement {
  const wrap = document.createElement('section');
  wrap.className = 'view-container';
  wrap.setAttribute('aria-busy', 'true');
  wrap.setAttribute('aria-label', 'Loading countdowns');

  const meta = document.createElement('div');
  meta.className = 'view-meta';
  meta.innerHTML = `
    <h2>Active countdowns</h2>
    <span class="count"><b>—</b>&nbsp; loading</span>
  `;
  wrap.appendChild(meta);

  const grid = document.createElement('div');
  grid.className = 'skeleton-grid';
  for (let i = 0; i < count; i++) {
    const sk = document.createElement('div');
    sk.className = 'skeleton-card';
    sk.style.setProperty('--i', String(i));
    sk.innerHTML = `
      <div class="sk-row">
        <div class="sk-icon"></div>
        <div class="sk-title skeleton-bar"></div>
        <div class="sk-badge skeleton-bar"></div>
      </div>
      <div class="sk-digits">
        <div class="sk-digit skeleton-bar"></div>
        <div class="sk-digit skeleton-bar"></div>
        <div class="sk-digit skeleton-bar"></div>
        <div class="sk-digit skeleton-bar"></div>
      </div>
      <div class="sk-footer">
        <div class="sk-date skeleton-bar"></div>
        <div class="sk-actions">
          <div class="sk-action skeleton-bar"></div>
          <div class="sk-action skeleton-bar"></div>
          <div class="sk-action skeleton-bar"></div>
        </div>
      </div>
    `;
    grid.appendChild(sk);
  }
  wrap.appendChild(grid);
  return wrap;
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(input: string): string {
  return escapeHtml(input);
}
